import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Badge } from 'react-bootstrap';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const PRIORITY_LEVELS = {
    LOW: 'Low',
    MEDIUM: 'Medium', 
    HIGH: 'High',
    URGENT: 'Urgent'
};

const PRIORITY_TO_NUMBER = {
    LOW: 1,
    MEDIUM: 2,
    HIGH: 3,
    URGENT: 4
};

const NUMBER_TO_PRIORITY = {
    1: 'LOW',
    2: 'MEDIUM',
    3: 'HIGH',
    4: 'URGENT'
};

const EISENHOWER_QUADRANTS = {
    DO: 'Do (Urgent & Important)',
    DECIDE: 'Decide (Important, Not Urgent)',
    DELEGATE: 'Delegate (Urgent, Not Important)',
    DELETE: 'Delete (Neither Urgent nor Important)'
};

const KANBAN_STATUSES = {
    TODO: 'To Do',
    IN_PROGRESS: 'In Progress', 
    REVIEW: 'Review',
    DONE: 'Done'
};

const MOSCOW_CATEGORIES = {
    MUST: 'Must Have',
    SHOULD: 'Should Have',
    COULD: 'Could Have',
    WONT: "Won't Have"
};

const CreateTaskModal = ({ show, handleClose, onCreateTask, profileTechniques, activeProfileId }) => {
    const [title, setTitle] = useState('');
    const [overview, setOverview] = useState('');
    const [acceptanceCriteria, setAcceptanceCriteria] = useState('');
    const [priority, setPriority] = useState('MEDIUM');
    const [eisenhowerQuadrant, setEisenhowerQuadrant] = useState('DO');
    const [kanbanStatus, setKanbanStatus] = useState('TODO');
    const [moscowCategory, setMoscowCategory] = useState('MUST');
    const [dueDate, setDueDate] = useState('');
    const [selectedLabels, setSelectedLabels] = useState([]);
    const [availableLabels, setAvailableLabels] = useState([]);
    
    // Time blocking fields
    const [scheduledDate, setScheduledDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [estimatedDuration, setEstimatedDuration] = useState('');

    useEffect(() => {
        if (show && activeProfileId) {
            fetchLabels();
        }
    }, [show, activeProfileId]);

    const fetchLabels = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/labels`);
            setAvailableLabels(response.data);
        } catch (error) {
            console.error('Error fetching labels:', error);
        }
    };

    const toggleLabel = (labelId) => {
        setSelectedLabels(prev => 
            prev.includes(labelId) 
                ? prev.filter(id => id !== labelId)
                : [...prev, labelId]
        );
    };

    const handleSubmit = () => {
        const taskData = {
            title,
            overview,
            acceptance_criteria: acceptanceCriteria,
            priority_level: PRIORITY_TO_NUMBER[priority],
            due_date: dueDate || null,
            labels: selectedLabels,
            ...(profileTechniques?.primaryPrioritization === 'EISENHOWER' && {
                eisenhower_quadrant: eisenhowerQuadrant
            }),
            ...(profileTechniques?.primaryPrioritization === 'KANBAN' && {
                kanban_status: kanbanStatus
            }),
            ...(profileTechniques?.primaryPrioritization === 'MOSCOW' && {
                moscow_category: moscowCategory
            }),
            // Time blocking fields
            ...(profileTechniques?.primaryTimeManagement === 'TIME_BLOCKING' && {
                scheduled_date: scheduledDate || null,
                scheduled_start_time: startTime || null,
                scheduled_end_time: endTime || null,
                estimated_duration: estimatedDuration ? parseInt(estimatedDuration) : null
            })
        };
        onCreateTask(taskData);
        setTitle('');
        setOverview('');
        setAcceptanceCriteria('');
        setPriority('MEDIUM');
        setEisenhowerQuadrant('DO');
        setKanbanStatus('TODO');
        setMoscowCategory('MUST');
        setDueDate('');
        setSelectedLabels([]);
        setScheduledDate('');
        setStartTime('');
        setEndTime('');
        setEstimatedDuration('');
        handleClose();
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Create New Task</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Task Title *</Form.Label>
                        <Form.Control
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="One line heading"
                            required
                        />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>Overview *</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={overview}
                            onChange={(e) => setOverview(e.target.value)}
                            placeholder="Detailed overview of the task"
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Acceptance Criteria *</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={acceptanceCriteria}
                            onChange={(e) => setAcceptanceCriteria(e.target.value)}
                            placeholder="What defines completion of this task?"
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Labels</Form.Label>
                        <div className="d-flex flex-wrap gap-2">
                            {availableLabels.map(label => (
                                <span
                                    key={label.id}
                                    style={{ 
                                        backgroundColor: selectedLabels.includes(label.id) ? label.color : '#f8f9fa',
                                        color: selectedLabels.includes(label.id) ? 'white' : '#6c757d',
                                        cursor: 'pointer',
                                        border: `1px solid ${label.color}`,
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        display: 'inline-block'
                                    }}
                                    onClick={() => toggleLabel(label.id)}
                                >
                                    {label.name}
                                </span>
                            ))}
                            {availableLabels.length === 0 && (
                                <small className="text-muted">No labels available. Create labels in Settings tab.</small>
                            )}
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Due Date (Optional)</Form.Label>
                        <Form.Control
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                        />
                    </Form.Group>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Priority</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={priority}
                                    onChange={(e) => setPriority(e.target.value)}
                                >
                                    {Object.entries(PRIORITY_LEVELS).map(([key, name]) => (
                                        <option key={key} value={key}>{name}</option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        
                        {profileTechniques?.primaryPrioritization === 'EISENHOWER' && (
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Eisenhower Quadrant</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={eisenhowerQuadrant}
                                        onChange={(e) => setEisenhowerQuadrant(e.target.value)}
                                    >
                                        {Object.entries(EISENHOWER_QUADRANTS).map(([key, name]) => (
                                            <option key={key} value={key}>{name}</option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                        )}
                        
                        {profileTechniques?.primaryPrioritization === 'KANBAN' && (
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Kanban Status</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={kanbanStatus}
                                        onChange={(e) => setKanbanStatus(e.target.value)}
                                    >
                                        {Object.entries(KANBAN_STATUSES).map(([key, name]) => (
                                            <option key={key} value={key}>{name}</option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                        )}
                        
                        {profileTechniques?.primaryPrioritization === 'MOSCOW' && (
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>MoSCoW Category</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={moscowCategory}
                                        onChange={(e) => setMoscowCategory(e.target.value)}
                                    >
                                        {Object.entries(MOSCOW_CATEGORIES).map(([key, name]) => (
                                            <option key={key} value={key}>{name}</option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                        )}
                    </Row>

                    {/* Time Blocking Fields */}
                    {profileTechniques?.primaryTimeManagement === 'TIME_BLOCKING' && (
                        <>
                            <h6 className="mb-3 text-primary">Time Block Schedule</h6>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Scheduled Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={scheduledDate}
                                            onChange={(e) => setScheduledDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Estimated Duration (minutes)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={estimatedDuration}
                                            onChange={(e) => setEstimatedDuration(e.target.value)}
                                            placeholder="e.g., 60"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Start Time</Form.Label>
                                        <Form.Control
                                            type="time"
                                            value={startTime}
                                            onChange={(e) => setStartTime(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>End Time</Form.Label>
                                        <Form.Control
                                            type="time"
                                            value={endTime}
                                            onChange={(e) => setEndTime(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                <Button variant="primary" onClick={handleSubmit} disabled={!title.trim() || !overview.trim() || !acceptanceCriteria.trim()}>
                    Create Task
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateTaskModal;
